import React from "react";
import { Link } from "react-router-dom";
import LegalPageLayout from "./LegalPageLayout";

/**
 * BUDDY AVANZA — Aviso de privacidad para estudiantes (age-appropriate layer).
 *
 * Diseñado para que estudiantes y responsables lo entiendan de forma
 * sencilla, sin reemplazar la Política de Privacidad completa.
 * Enlaza al documento formal para las secciones legales completas.
 */
export default function BuddyAvanzaStudentPrivacy() {
  return (
    <LegalPageLayout
      documentKey="privacy"
      testid="legal-student-privacy"
      eyebrow="Información en lenguaje simple"
      documentTitle="Tu privacidad en BUDDY AVANZA"
      intro="Este es un resumen sencillo para que tú, y quien te acompaña, entiendan cómo cuidamos tu información dentro de BUDDY AVANZA. Si quieres leer la versión completa, encontrarás el enlace al final."
    >
      {({ contactEmail, operator }) => (<>
        <section>
          <h2>¿Qué información usamos?</h2>
          <ul>
            <li>Tu correo, tu grado y tu edad, para poder crear tu cuenta.</li>
            <li>El correo de tu padre, madre o adulto responsable si eres menor de 18 años.</li>
            <li>Tu avance dentro de BUDDY AVANZA: qué materias practicas, qué ejercicios haces y cómo te va.</li>
          </ul>
        </section>

        <section>
          <h2>¿Para qué la usamos?</h2>
          <ul>
            <li>Para que puedas entrar a la plataforma y tener tu cuenta.</li>
            <li>Para adaptar la práctica a lo que necesitas aprender.</li>
            <li>Para que Buddy pueda acompañarte cuando necesitas ayuda.</li>
            <li>Para mantener el servicio seguro.</li>
          </ul>
        </section>

        <section>
          <h2>¿Cómo funciona Buddy y la inteligencia artificial?</h2>
          <p>Buddy usa tecnología de inteligencia artificial para ayudarte
             a razonar. Cuando le pides ayuda, BUDDY AVANZA solamente le
             comparte el contexto pedagógico necesario (la materia, la
             habilidad, la pregunta y las opciones). <strong>No le
             comparte</strong> tu nombre, tu correo, tu fecha de nacimiento
             ni los datos de tu adulto responsable.</p>
          <p>Buddy puede equivocarse: no es infalible y no reemplaza a tu
             docente. Úsalo como acompañamiento, no como reemplazo del
             estudio.</p>
        </section>

        <section>
          <h2>¿Quién más puede procesar información?</h2>
          <p>{operator} trabaja con proveedores externos que ayudan a que
             BUDDY AVANZA funcione: infraestructura tecnológica, base de
             datos, envío de correos operativos, procesamiento de pagos y
             el proveedor del modelo de inteligencia artificial. Algunos de
             estos proveedores están fuera de El Salvador. Solo reciben la
             información necesaria para su tarea y bajo estándares
             reconocidos de seguridad. Los detalles están en las secciones
             11 y 12 de la Política de Privacidad completa.</p>
        </section>

        <section>
          <h2>¿Cuáles son tus derechos?</h2>
          <p>Puedes pedirle a {operator} lo siguiente sobre tu información:</p>
          <ul>
            <li>Saber qué información tiene sobre ti (acceso).</li>
            <li>Corregirla si algo está mal (rectificación).</li>
            <li>Pedir que se elimine cuando corresponda (cancelación).</li>
            <li>Oponerte a algún tratamiento en supuestos aplicables.</li>
            <li>Pedir una copia en un formato común cuando aplique (portabilidad).</li>
            <li>Retirar el consentimiento que se dio anteriormente.</li>
          </ul>
          <p>Si eres menor de 18 años, tu padre, madre, tutor legal o adulto
             responsable puede hacer estas solicitudes por ti.</p>
        </section>

        <section>
          <h2>¿Cómo se hace una solicitud?</h2>
          <p>Escribiendo un correo a{" "}
            <a href={`mailto:${contactEmail}`} data-testid="student-privacy-contact-email">{contactEmail}</a>{" "}
            con el asunto <em>[PRIVACIDAD] BUDDY AVANZA</em>. Podríamos
            pedirte información adicional para confirmar tu identidad —o la
            relación con el estudiante— antes de atender la solicitud.
            Algunas categorías de información pueden estar sujetas a plazos
            legales de conservación y podrían no eliminarse mientras esas
            obligaciones sigan vigentes; en ese caso te lo explicaremos.</p>
        </section>

        <section>
          <h2>Documento completo</h2>
          <p>Este es un resumen. La versión formal, con todas las
             secciones legales, está en la{" "}
            <Link to="/buddy-avanza/privacidad" data-testid="student-privacy-full-link">
              Política de Privacidad completa
            </Link>.
             Sobre el uso de inteligencia artificial, consulta también el{" "}
             <Link to="/buddy-avanza/inteligencia-artificial" data-testid="student-privacy-ai-link">
               Aviso de Inteligencia Artificial
             </Link>.</p>
        </section>
      </>)}
    </LegalPageLayout>
  );
}
